import { useQuery } from '@tanstack/react-query';
import { useAtom, useSetAtom } from 'jotai';
import { atomFamily, atomTree } from 'jotai-family';
import { atomWithStorage, useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';
import { match } from 'ts-pattern';
import { queryMe } from '../me/api';
import type { ScriptDto } from '../script/script.dto';
import type { TaskDto } from '../task/model';
import { GenerationType } from '../video-model/model-spec';
import type { ScriptSource, VideoEditPageData } from './model';

export const generationDataFamilyAtom = atomFamily(
  (params: {
    accountId: string;
    type: GenerationType;
    isLongVideoSupported?: boolean;
  }) => {
    const key = match(params)
      .with(
        {
          type: GenerationType.Frame,
        },
        () => 'frame_image',
      )
      .with(
        {
          type: GenerationType.Reference,
          isLongVideoSupported: true,
        },
        () => 'reference_image',
      )
      .with(
        {
          type: GenerationType.Reference,
        },
        () => 'long_reference_image',
      )
      .exhaustive();

    const tree = atomTree((path: string[]) =>
      atomWithStorage<
        ScriptDto & {
          simple_mode: boolean | undefined;
          source: ScriptSource | undefined;
        }
      >(
        `${path.join('_')}_page_data`,
        {
          id: null!,
          template_id: null,
          avatar_ids: [],
          aspect_ratio: '9:16',
          duration: 5,
          file_list: [],
          product_name: '',
          product_description: '',
          segments: [],
          prompt: '',
          generation_type: params.type,
          simple_mode: true,
          source: void 0,
          generate_audio: true,
        },
        void 0,
        {
          getOnInit: true,
        },
      ),
    );

    return tree([params.accountId, 'v2', key]);
  },
);

// ========== 视频编辑相关 ==========

/** 用于在 TaskResultItem 和编辑页面之间传递父任务数据 */
export const currentEditTaskAtom = atomWithStorage<TaskDto | null>(
  'current_edit_task_v2',
  null,
  void 0,
  { getOnInit: true },
);

/** 视频编辑页面的表单数据持久化 atom family，键为 accountId */
export const videoEditPageDataFamilyAtom = atomFamily(
  (params: { accountId: string }) => {
    const tree = atomTree((path: string[]) =>
      atomWithStorage<VideoEditPageData>(
        `${path.join('_')}_page_data`,
        {},
        void 0,
        { getOnInit: true },
      ),
    );

    return tree([params.accountId, 'v2', 'video_edit']);
  },
);

export function useReadVideoEditPageData() {
  const { data } = useQuery(queryMe);

  const read = useAtomCallback(
    useCallback(
      (get) => {
        const curr = get(videoEditPageDataFamilyAtom({ accountId: data.name }));
        return curr;
      },
      [data.name],
    ),
  );
  return read;
}

export function useSetVideoEditPageData() {
  const { data } = useQuery(queryMe);
  return useSetAtom(videoEditPageDataFamilyAtom({ accountId: data.name }));
}

export function useVideoEditPageData() {
  const { data } = useQuery(queryMe);
  return useAtom(videoEditPageDataFamilyAtom({ accountId: data.name }));
}
